import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FormatTypes } from '../../../../../Lib/Format';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const isIpTest = ({ worksheetType }: IConfigFunctionArgs) =>
  [qestIds.maximumDryDensity.ARIZ_225d_245c.rev2019].includes(worksheetType.Id);

const isWetDensityMassEntryUnitPounds = ({ worksheet }: IConfigFunctionArgs) =>
  worksheet.data.Preparation.WetDensityMassEntryUnit === 'pounds';

const getDefaultReadability = (configArgs: IConfigFunctionArgs) =>
  isIpTest(configArgs) && isWetDensityMassEntryUnitPounds(configArgs)
    ? { defaultReadability: 0.001, defaultUom: 'lb' }
    : { defaultReadability: 1, defaultUom: 'g' };

const config = () =>
  mergeConfig(
    {
      // Fields
      ...getConfigForMeasuredTest(
        qestIds.oversizeDetermination.ARIZ_225d_245c,
        'data.Preparation.AssociatedOversizeDetermination'
      ),
      ...getConfigForMeasuredTest(
        qestIds.specificGravityCoarse.AASHTO_T_85.rev2014,
        'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse'
      ),
      'data.Preparation.OversizeDeterminationResults.Sieves.CumulativeDryMassRetainedPercent': {
        format: { nearest: 1 },
      },
      'data.Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves.CumulativeDryMassRetainedPercent': {
        format: { nearest: 0.1 },
      },
      ...getConfigForImportedTest(
        qestIds.specificGravityCoarse.AASHTO_T_85.rev2014,
        'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse'
      ),
      'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.SpecificGravityOvenDry':
        {
          format: { nearest: 0.001 },
        },
      'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.Absorption': {
        format: { nearest: 0.01 },
      },
      'data.Preparation.ScreeningASTM.OversizePercent': {
        format: { nearest: 1.0 },
      },
      'data.Preparation.MinimumOversizeForCorrection': {
        format: { nearest: 0.1 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravityOvenDry': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.AssumedSpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.AssumedAbsorption': {
        format: { nearest: 0.01 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravitySSD': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.ApparentSpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.Absorption': {
        format: { nearest: 0.01 },
      },

      ...getConfigForTest(qestIds.moistureContent.AASHTO_T_265.rev2015, 'data.TestPortions.MoistureDetermination'),

      'mddChart.XAxisMajorStep': { format: { nearest: 0.1 } },
      'mddChart.XAxisMin': { format: { nearest: 0.1 } },
      'cwdChart.XAxisMajorStep': { format: { nearest: 0.1 } },
      'cwdChart.XAxisMin': { format: { nearest: 0.1 } },

      'data.Preparation.EnterConvertedWetDensity': { label: 'Enter estimated dry density data' },

      'data.grid.CompactionData': (wsDetails: IConfigFunctionArgs) => {
        const isIp = isIpTest(wsDetails);
        const isMassEntryUnitPounds = isWetDensityMassEntryUnitPounds(wsDetails);
        const weightUnit = isIp && isMassEntryUnitPounds ? 'lb' : 'g';
        const volumeUnit = isIp ? 'ft³' : 'cm³';
        const weightVolumeUnit = isIp ? 'lb/ft³' : 'kg/m³';
        return {
          rowHeaders: [
            '',
            'Mold',
            `Mass (${weightUnit})`,
            `Volume (${volumeUnit})`,
            `Portion Mass (${weightUnit})`,
            'Water Added Target (%)',
            `Water to Add (${weightUnit})`,
            `Water Added (${weightUnit})`,
            'Actual Water Added (%)',
            `Wet Soil and Mold Mass (${weightUnit})`,
            `Wet Soil Mass (${weightUnit})`,
            `Wet Density (${weightVolumeUnit})`,
          ],
        };
      },

      'data.TestPortions.WaterAddedTargetPercent': { format: { nearest: 1 } },
      'data.TestPortions.WaterAddedPercent': { format: { nearest: 0.1 } },
      'data.Equipment.HammerFaceDiameter': (configArgs: IConfigFunctionArgs) => {
        const units = oPath.get(configArgs.worksheet, `data.Equipment.HammerFaceNominalUnits`);
        return {
          format: units === 'Metric' ? { nearest: 0.01 } : { nearest: 0.001 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      'data.Equipment.HammerFaceRadius': (configArgs: IConfigFunctionArgs) => {
        const units = oPath.get(configArgs.worksheet, `data.Equipment.HammerFaceNominalUnits`);
        return {
          format: units === 'Metric' ? { nearest: 0.01 } : { nearest: 0.001 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
    },
    getLabelConfig('data'),
    getMeasuredUsingConfig('data'),
    getDerivedFromMeasurementsConfig('data'),
    {
      'data.Equipment.CompactionBalance': getDefaultReadability,
    }
  );

const ip = {
  'data.Equipment.MouldVolume': { uom: 'ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.TestPortions.WaterAddedPercent': { format: { nearest: 0.1 } },
  'data.TestPortions.MouldVolume': { uom: 'ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.WetDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.DryDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.Results.MaximumDryDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.Results.OptimumMoistureContent': { format: { nearest: 0.1 } },
};

const getConfig = (_: string) => mergeConfig(config(), ip);

export default {
  [qestIds.maximumDryDensity.ARIZ_225d_245c.rev2019]: getConfig,
};
